import { Button } from '@design-system';
import classnames from 'classnames';
import { useTranslation } from 'next-i18next';
import React from 'react';

import { PLACE_HOLDER_IMAGE } from '../../../constants';
import { pathFor } from '../../../helpers/links';
import { useIsSubscribed } from '../../../hooks/subscription';
import type { AugmentedArtistCard } from '../../../types/Artists';
import { GeneratedSubscriptionType } from '../../../types/generated';
import { getArtistTileOverline } from '../../../utils/artists';
import { formatStoryblokSlug } from '../../../utils/slug';
import { ArtistProfileCard } from '../../ArtistProfileCard/ArtistProfileCard';

type Props = {
  artists: Array<AugmentedArtistCard>;
  className?: string;
  onArtistClick: (
    index: number,
    uuid: string,
    name: string,
    link: string,
  ) => void;
  onViewAllArtistsClick: () => void;
  title: string;
};

const RelatedArtists = ({
  artists,
  className,
  onArtistClick,
  onViewAllArtistsClick,
  title,
}: Props) => {
  const { t } = useTranslation('artists');

  const { checkIsSubscribed } = useIsSubscribed(
    artists.map((a) => ({
      objectId: a.internalId ?? '',
      type: GeneratedSubscriptionType.Artist,
    })),
  );

  return !artists.length ? null : (
    <div className={classnames(className, 'flex w-full flex-col gap-4')}>
      <div className="flex w-full items-center gap-3">
        <h2 className="w-full flex-1 text-md text-neutral-4 md:text-lg">
          {title}
        </h2>

        <div className="shrink-0">
          <Button
            as="a"
            href={pathFor({ page: 'ARTISTS_INDEX' })}
            label={t('viewAllArtists')}
            onClick={onViewAllArtistsClick}
            size="sm"
            variant="tertiary"
          />
        </div>
      </div>
      <div className="grid-section w-full flex-1 gap-y-6">
        {artists.map(
          (
            {
              alt,
              focus,
              galleryImages,
              hasUpcomingRelease,
              hideCollaborations,
              image,
              internalId,
              link,
              name,
              totalWorks,
              uuid,
            },
            index,
          ) => (
            <div key={name} className="col-span-6 md:col-span-4 lg:col-span-2">
              <ArtistProfileCard
                href={formatStoryblokSlug(link)}
                image={{
                  alt: alt || name,
                  focus: focus || undefined,
                  hoverSrc: galleryImages?.[0],
                  src: image || PLACE_HOLDER_IMAGE,
                }}
                isSubscribed={checkIsSubscribed(
                  internalId,
                  GeneratedSubscriptionType.Artist,
                )}
                name={name}
                onClick={() => onArtistClick(index, uuid, name, link)}
                overline={hasUpcomingRelease ? t('artistUpcomingRelease') : ''}
                subtitle={getArtistTileOverline(
                  totalWorks,
                  hideCollaborations,
                  t,
                )}
              />
            </div>
          ),
        )}
      </div>
    </div>
  );
};

export default RelatedArtists;
